import PageErrorFallback from "@/components/error/PageErrorFallback";
import { useAuth0Context } from "@/context/Auth0Context";
import { useRoleBasedNavigation } from "@/hooks/auth/useRoleBasedNavigation";
import { useMixpanelIdentify } from "@/hooks/user/useMixpanelIdentify";
import LoadingPage from "@/pages/common/LoadingPage";
import React, { Suspense } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Navigate, Route, Routes, useLocation } from "react-router";
import { AuthenticationGuard } from "./AuthenticationGuard";
import { ProfileCompletionGuard } from "./ProfileCompletionGuard";
import { PUBLIC_ROUTES } from "./router";

const AppRouter: React.FC = () => {
  const { isAuthenticated, isLoading, isInitialized, user, primaryRole } =
    useAuth0Context();
  const { availableRoutes, getDefaultDashboard } = useRoleBasedNavigation();
  const location = useLocation();

  // Identify user in Mixpanel when authenticated
  useMixpanelIdentify();

  if (isLoading || !isInitialized) {
    return <LoadingPage />;
  }

  return (
    <ErrorBoundary
      FallbackComponent={PageErrorFallback}
      resetKeys={[location.pathname, isAuthenticated]}
    >
      <Suspense fallback={<LoadingPage />}>
        <Routes>
          {/* Public routes (login, 404, etc.) */}
          {PUBLIC_ROUTES.map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={<route.component />}
            />
          ))}

          {isAuthenticated &&
            availableRoutes.map((route) => (
              <Route
                key={route.path}
                path={route.path}
                element={
                  <AuthenticationGuard
                    component={() => (
                      <ProfileCompletionGuard requiresProfile={true}>
                        <route.component />
                      </ProfileCompletionGuard>
                    )}
                  />
                }
              />
            ))}

          {/* Fallback, may be redundant */}
          <Route
            path="*"
            element={
              isAuthenticated ? (
                <Navigate to={getDefaultDashboard} replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
};

export default AppRouter;
